import React, { memo, useState } from 'react';
import classNames from 'classnames';
import _sortBy from 'lodash/sortBy';
import _uniqBy from 'lodash/uniqBy';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { v4 } from 'uuid';

import { buildCmpSubpagesUrls, promisesSettled } from 'utils';

import ZpFilter from '../../../components/zpFilter';
import { CAROUSEL_FILTERS, FILTER_PARAMS } from '../../../components/zpFilter/filterUtils';
import CommonService from '../../../services/commonService';
import { CMP_SUBPAGES, JOB_BUCKET_GROUP } from '../../../utils/constant';

import ChartTable from './chartTable';

import classes from './genderRatioComparision.module.css';

const ReactTooltip = dynamic(() => import('react-tooltip'), { ssr: false });

const xLabels = [
  { female: '0%', marginF: 47, male: '100%', marginM: 31 },
  { female: '25%', marginF: 39, male: '75%', marginM: 39 },
  { female: '50%', marginF: 39, male: '50%', marginM: 39 },
  { female: '75%', marginF: 39, male: '25%', marginM: 39 },
  { female: '100%', marginF: 0, male: '0%', marginM: 0 },
];

const heads = [
  { title: 'Job Title', value: 'layTitle' },
  { title: 'Male', value: 'male' },
  { title: 'Female', value: 'female' },
];

const filterGroup = JOB_BUCKET_GROUP.CAREER_DEMOGRAPHICS_COMPARE_GENDER_RATIO;

const GenderRatioComparision = ({
  jobTitle,
  genderRatio,
  compareGenderContentBlurb,
  filterValueRange,
  filterLogEventCallback,
  jobsIndex,
  customFilter,
  customFilterProps = {},
  hideContent = false,
  customLink,
}) => {
  function createRelatedTitles(newRelatedTitles = []) {
    const { relatedTitles, ...rest } = genderRatio;
    const relatedTitlesArray = relatedTitles && relatedTitles.length > 0 ? [...relatedTitles] : [];
    return _uniqBy(
      _sortBy(
        [
          { ...rest, layTitle: jobTitle, className: classes['selected'], isIndex: jobsIndex },
          ...newRelatedTitles,
          ...relatedTitlesArray,
        ],
        ['male']
      ),
      'layTitle'
    ).reverse();
  }

  const [relatedTitles, setRelatedTitles] = useState(createRelatedTitles());

  const handleFilterUpdate = async (_, values) => {
    if (customFilter) {
      const response = await customFilter(values);
      setRelatedTitles(createRelatedTitles(response));
      return;
    }
    const value = values.value;
    const genderRatioByTitle = value && value.originalSearch ? value.originalSearch : value;

    try {
      if (!genderRatioByTitle || !genderRatioByTitle.length) {
        setRelatedTitles(createRelatedTitles());
        return;
      }

      if (jobTitle === genderRatioByTitle) {
        return;
      }

      const data = {
        jobTitles: [genderRatioByTitle],
      };

      const promises = [
        CommonService.getTitlesIndexes(genderRatioByTitle),
        CommonService.fetchGenderRatioBarChartData(data),
      ];

      const [indexes, searchTitleInfo] = await promisesSettled(promises);

      const newRelatedTitles = searchTitleInfo.map((relatedTitle, idx) => ({
        ...relatedTitle,
        layTitle: data.jobTitles[idx],
        className: classes['selected'],
        isIndex: indexes?.[genderRatioByTitle].indexInfoOfJobsPage === 'INDEX',
      }));

      setRelatedTitles(createRelatedTitles(newRelatedTitles));
    } catch (error) {
      console.error(error);
    }
  };

  const filterProps = () => {
    return {
      ...CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TITLE],
      buttonText: 'Compare Jobs',
      valueRange: filterValueRange,
      onApply: handleFilterUpdate,
      onClear: handleFilterUpdate,
      options: filterLogEventCallback && {
        ...CAROUSEL_FILTERS[FILTER_PARAMS.JOB_TITLE].options,
        logEvent: () => filterLogEventCallback(filterGroup),
      },
      ...customFilterProps,
    };
  };

  return (
    <section id="gender-ratio-comparision" className={classes['gender-ratio-comparision']}>
      {!hideContent && (
        <>
          <div className="z-mt-68" />
          <h3 className="cmpSectionh4 mb-3 text-capitalize">{`${jobTitle} related jobs gender ratio`}</h3>
        </>
      )}

      {compareGenderContentBlurb && compareGenderContentBlurb.content && (
        <div dangerouslySetInnerHTML={{ __html: compareGenderContentBlurb.content }} />
      )}

      <div className="mt-4">
        <ZpFilter {...filterProps()} />
      </div>

      <div className="d-block d-lg-none">
        <ChartTable
          heads={heads}
          tableData={relatedTitles}
          defaultSortedField="male"
          formatter={(value) => {
            return value ? `${(value * 100).toFixed(0)}%` : '-';
          }}
        />
      </div>

      <div className={classNames('d-none d-lg-block z-mt-24', classes['horizontal-chart'])}>
        <div className={classNames('mb-2', classes.legends)}>
          <div className={classNames('openSansBlackBold', classes['legends-male'])}>Male</div>
          <div className={classNames('openSansBlackBold', classes['legends-female'])}>Female</div>
        </div>
        <div className="d-flex position-relative w-100">
          <div className={classes['x-labels']}>
            {xLabels.map(({ male, marginM }, idx) => (
              <div key={idx} className="d-flex flex-column justify-content-end position-relative">
                <div className={classes['grid-line']} />
                <div
                  className={classNames('lightTextStyle2', classes['x-label'])}
                  style={{ marginRight: marginM }}
                >
                  {male}
                </div>
              </div>
            ))}
          </div>
          <div className={classNames('my-3', classes['y-labels'])}>
            {relatedTitles.map((relatedTitle) => (
              <div
                key={v4()}
                className={classNames('d-flex align-items-center', classes['h-bar-container'])}
              >
                <div className={classNames('lightTextStyle2 text-capitalize', classes['y-label'])}>
                  {relatedTitle.isIndex ? (
                    <a
                      className="alink"
                      href={
                        customLink
                          ? customLink(relatedTitle)
                          : buildCmpSubpagesUrls(relatedTitle.layTitle, '', CMP_SUBPAGES.JOBS)
                      }
                      title={customLink ? '' : `${relatedTitle.layTitle} Jobs` || ''}
                    >
                      {relatedTitle.layTitle}
                    </a>
                  ) : (
                    <p>{relatedTitle.layTitle}</p>
                  )}
                </div>
                <div
                  className={classNames(classes['h-bar'], classes.male, {
                    [relatedTitle.className]: relatedTitle.className,
                  })}
                  data-tip
                  data-for={`male-${relatedTitle.layTitle}-idx`}
                  style={{ width: 267 * relatedTitle.male, left: 277 - 267 * relatedTitle.male }}
                />
                <ReactTooltip id={`male-${relatedTitle.layTitle}-idx`} place="top">
                  <p className="mb-0 text-light">
                    {relatedTitle.layTitle}:{' '}
                    <strong>{(100 * relatedTitle.male).toFixed(0)}%</strong>
                  </p>
                </ReactTooltip>
                <div
                  className={classNames(classes['h-bar'], classes.female, {
                    [relatedTitle.className]: relatedTitle.className,
                  })}
                  data-tip
                  data-for={`female-${relatedTitle.layTitle}-idx`}
                  style={{
                    width: 267 * relatedTitle.female,
                  }}
                />
                <ReactTooltip id={`female-${relatedTitle.layTitle}-idx`} place="top">
                  <p className="mb-0 text-light">
                    {relatedTitle.layTitle}:{' '}
                    <strong>{(100 * relatedTitle.female).toFixed(0)}%</strong>
                  </p>
                </ReactTooltip>
              </div>
            ))}
          </div>
          <div className={classes['x-labels']}>
            {xLabels.map(({ female, marginF }, idx) => (
              <div key={idx} className="d-flex flex-column justify-content-end position-relative">
                <div className={classes['grid-line']} />
                <div
                  className={classNames('lightTextStyle2', classes['x-label'])}
                  style={{ marginRight: marginF }}
                >
                  {female}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

GenderRatioComparision.propTypes = {
  jobTitle: PropTypes.string.isRequired,
  genderRatio: PropTypes.object.isRequired,
};

export default memo(GenderRatioComparision);
